import type { ChannelSummaryType } from 'ChannelSummary/types'
import classNames from 'classnames'
import { InfoElement } from 'components'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { storeNameColumn } from 'TableCommon/defaultColumns'
import { formatNumber, getAmountLabelForNumber, getDifferenceWithPrecision } from 'utils'

import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'

import FloorSpaceTooltip from './FloorSpaceTooltip'

const TitleIcons = storeNameColumn.RowRenderer

interface Props {
  data: {
    displayChannel: boolean
    maxRevenue: number
    showModal: (store: any) => void
    stores: any[]
    type: ChannelSummaryType
  }
  index: number
  style?: any
}

function RevenueStationaryTableRow({ data, index, style }: Props) {
  const { t: translate } = useTranslation()
  const { showModal, stores, maxRevenue } = data
  const store = stores[index]
  const percentOfMaxRevenue = Math.round((100 * store.revenueCurrentPLN) / maxRevenue)
  const modalHandler = () => {
    showModal(store)
  }
  const renderTooltip = (iconClassName: string, localeKey: string) => (
    <Tooltip title={translate(localeKey) as string} placement="top">
      <i className={classNames('fm', iconClassName)} />
    </Tooltip>
  )

  return (
    <Box
      style={style}
      onClick={modalHandler}
      className={classNames('row cursor-pointer', { 'is-temporary-closed': store.temporaryClosed })}
    >
      <div className="row-wrapper flex-row">
        <div className="cell-bar" style={{ width: `${percentOfMaxRevenue}%` }} />

        <div className="cell-title flex-row">
          <TitleIcons {...data} store={store} />
        </div>

        <div className="cell-value">
          <span>
            {store.revenueCurrent === null ? '-' : formatNumber(store.revenueCurrent, translate)}{' '}
            {getAmountLabelForNumber(store.revenueCurrent, translate, store.currency)}
          </span>
        </div>
        <div className="cell-category tablet-portrait-up-only">
          <span className={classNames('category', `is-${store.cluster === '-' ? 'none' : store.cluster.toLocaleLowerCase()}`)}>
            {store.cluster}
          </span>
        </div>
        <div className="cell-concept">
          <span>{store.concept}</span>
        </div>
        <div className="cell-difference">
          <InfoElement
            percentDifference={getDifferenceWithPrecision({
              handleNullCase: true,
              number: store.revenuePercent,
              precision: 0,
              translate,
            })}
          />
        </div>
        <div className="cell-esize cell-icons">{store.esize && <i className="fm fm-esize" />}</div>
        <div className="cell-icons cell-surfchange">
          {store.floorSpaceIncreased && (
            <FloorSpaceTooltip iconClassName="fm-surfchange-up" localeKey="revenue.floorSpaceIncreased" translate={translate} />
          )}
          {store.floorSpaceDecreased && (
            <FloorSpaceTooltip iconClassName="fm-surfchange-down" localeKey="revenue.floorSpaceDecreased" translate={translate} />
          )}
        </div>
        <div className="cell-icons">
          {store.lifting && renderTooltip('fm-lifting', 'revenue.lifting')}
          {!store.lifting && store.new && renderTooltip('fm-new-shop', 'revenue.new')}
        </div>
        <div className="cell-icons">{store.promotion && renderTooltip('fm-promotions', 'revenue.promotion')}</div>
        <div className="cell-icons">{store.fair}</div>
        <div className="cell-icons">{store.express && renderTooltip('fm-ccc-express', 'revenue.express')}</div>
      </div>
    </Box>
  )
}

export default RevenueStationaryTableRow
